import { useMutation } from "@tanstack/react-query";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import userService, { type SignInReq } from "@/api/services/userService";
import { loadNotificationsFromAPI } from "@/store/notificationStore";
import { clearFileToken, setFileToken } from "@/utils/file-token";

import { toast } from "sonner";
import type { UserInfo, UserToken } from "#/entity";
import { StorageEnum } from "#/enum";

type ImpersonateState = {
	isImpersonating: boolean;
	originalToken: string | null;
	originalUser: Partial<UserInfo> | null;
	originalPermissions: string[];
};

type UserStore = {
	userInfo: Partial<UserInfo>;
	userToken: UserToken;
	permissions: string[];
	impersonate: ImpersonateState;

	actions: {
		setUserInfo: (userInfo: UserInfo) => void;
		setUserToken: (token: UserToken) => void;
		setPermissions: (permissions: string[]) => void;
		clearUserInfoAndToken: () => void;
		startImpersonate: (token: string, user: UserInfo, permissions: string[]) => void;
		stopImpersonate: () => void;
	};
};

const useUserStore = create<UserStore>()(
	persist(
		(set, get) => ({
			userInfo: {},
			userToken: {},
			permissions: [],
			impersonate: {
				isImpersonating: false,
				originalToken: null,
				originalUser: null,
				originalPermissions: [],
			},
			actions: {
				setUserInfo: (userInfo) => {
					set({ userInfo });
				},
				setUserToken: (userToken) => {
					set({ userToken });
				},
				setPermissions: (permissions) => {
					set({ permissions });
				},
				clearUserInfoAndToken() {
					clearFileToken();
					set({
						userInfo: {},
						userToken: {},
						permissions: [],
						impersonate: {
							isImpersonating: false,
							originalToken: null,
							originalUser: null,
							originalPermissions: [],
						},
					});
				},
				startImpersonate: (token, user, permissions) => {
					const state = get();
					set({
						impersonate: {
							isImpersonating: true,
							originalToken: state.userToken.accessToken || null,
							originalUser: state.userInfo,
							originalPermissions: state.permissions,
						},
						userToken: { accessToken: token },
						userInfo: { id: user.id.toString(), name: user.name, username: user.name },
						permissions,
					});
					sessionStorage.setItem("token", token);
					sessionStorage.setItem("permissions", JSON.stringify(permissions));
					sessionStorage.setItem("user", JSON.stringify(user));
				},
				stopImpersonate: () => {
					const state = get();
					const imp = state.impersonate;
					if (imp.originalToken && imp.originalUser) {
						set({
							userToken: { accessToken: imp.originalToken },
							userInfo: imp.originalUser,
							permissions: imp.originalPermissions,
							impersonate: {
								isImpersonating: false,
								originalToken: null,
								originalUser: null,
								originalPermissions: [],
							},
						});
						sessionStorage.setItem("token", imp.originalToken);
						sessionStorage.setItem("permissions", JSON.stringify(imp.originalPermissions));
						sessionStorage.setItem("user", JSON.stringify(imp.originalUser));
					}
				},
			},
		}),
		{
			name: "userStore", // name of the item in the storage (must be unique)
			storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used
			partialize: (state) => ({
				[StorageEnum.UserInfo]: state.userInfo,
				[StorageEnum.UserToken]: state.userToken,
				permissions: state.permissions,
				impersonate: state.impersonate,
			}),
		},
	),
);

export const useUserInfo = () => useUserStore((state) => state.userInfo);
export const useUserToken = () => useUserStore((state) => state.userToken);
export const useUserPermissions = () => useUserStore((state) => state.permissions);
export const useUserRoles = () => useUserStore((state) => state.userInfo.roles || []);
export const useUserActions = () => useUserStore((state) => state.actions);
export const useImpersonateState = () => useUserStore((state) => state.impersonate);

export const useSignIn = () => {
	const { setUserToken, setUserInfo, setPermissions } = useUserActions();

	const signInMutation = useMutation({
		mutationFn: userService.signin,
	});

	const signIn = async (data: SignInReq) => {
		try {
			const res = await signInMutation.mutateAsync(data);

			// Validate response structure - only check if response exists but is missing required fields
			if (!res || !res.message || res.message !== "Sikeres bejelentkezés") {
				throw new Error(res.message);
			}
		

			const { token, permissions, user } = res;

			// Save token to sessionStorage
			sessionStorage.setItem("token", token);

			// Fájl-hozzáférési token: ezzel tölti be a felület a képeket és
			// dokumentumokat. A szerver adja, nem mi számoljuk.
			if (res.file_token) setFileToken(res.file_token);

			// Save permissions to sessionStorage
			sessionStorage.setItem("permissions", JSON.stringify(permissions));

			// Save user data to sessionStorage
			sessionStorage.setItem("user", JSON.stringify(user));

			// Update store
			setUserToken({ accessToken: token });
			setPermissions(permissions);
			setUserInfo({ id: user.id.toString(), name: user.name, username: user.name, email: data.email });

			// Load unread notifications from API
			loadNotificationsFromAPI().catch((error) => {
				console.error("Failed to load notifications:", error);
				// Don't block login if notifications fail
			});

			// Don't throw error on success
		} catch (err) {
			// Extract error message - prioritize API error message from userService.signin
			let errorMessage = "An error occurred";
			if (err instanceof Error) {
				// The Error object from userService.signin already contains the API message
				// Only use "Invalid response from server" if it's our validation error
				errorMessage = err.message;
			} else if (err && typeof err === "object") {
				// Handle other error types (e.g., Axios errors)
				errorMessage = (err as any).response?.data?.message || (err as any).message || errorMessage;
			}
			
			toast.error(errorMessage, {
				position: "top-center",
			});
			throw err;
		}
	};

	return signIn;
};

export default useUserStore;
